import type React from 'react';
import { useTranslation } from 'react-i18next';
import { Input, Label, ListBox, Select, TextField } from '@heroui/react';
import type { LocaleCode, OperationCategory } from '@/api/models';
import { getNodeTestIdProp } from '@/utils/nodeTestProps';
import { categoryOptions } from '../../../../operationCategoryMeta';
import type { SubtitleClause } from '../../operationEditorMeta';

type EditorHeaderProps = {
    nameKa: string;
    nameEn: string;
    onNameKaChange: (value: string) => void;
    onNameEnChange: (value: string) => void;
    /** The tenant's whole set, in display order. Empty while it loads. */
    categories: OperationCategory[];
    categoryId: string;
    onCategoryChange: (categoryId: string) => void;
    /** Which language reads as the title; the other reads as the secondary line. */
    locale: LocaleCode;
    /** A BranchAdmin reads the recipe; the name is read-only for them too. */
    isReadOnly: boolean;
    /** Already filtered by `subtitleClauses` — unknown facts are absent, not blank. */
    subtitle: SubtitleClause[];
    /** The toolbar, passed in rather than rendered here — see below. */
    actions: React.ReactNode;
};

const NAME_INPUT = 'text-xl font-bold tracking-tight';
const SECONDARY_NAME_INPUT = 'text-sm text-muted';

/**
 * The template's name in both languages, what it currently is, and what you
 * can do to it.
 *
 * **Both languages are editable, always** — not just while creating a
 * template. A rename is safe at any point: a posted entry captures its own
 * description at record time rather than reading the template's current
 * name live, so renaming later cannot retroactively change how history
 * reads, the way editing a published recipe's pairs would.
 *
 * The subtitle is assembled from clauses that are **dropped rather than
 * faked** when their fact is missing: a template that has never published
 * says so by omitting the "in force since" clause, not by printing a dash
 * where a version number goes. `subtitleParts` decides what is known; this
 * decides how it reads.
 *
 * The actions arrive as a node instead of being rendered here because the
 * toolbar owns a confirm dialog and a mutation's worth of disabled states, and
 * a header that reached into those would be a header that knows about
 * publishing. It only knows where the actions sit.
 *
 * **The category sits with the name, not with the recipe.** It is a label on
 * the operation's identity — like the name, and unlike a pair — so refiling one
 * changes nothing about how a past entry reads and needs no version. It is
 * absent rather than empty while the set loads: a picker with no options is a
 * control that looks broken.
 *
 * **The title follows the active UI language**, same as before the name
 * became editable: the field showing Georgian or English on top swaps with
 * `locale`, the other renders smaller underneath. Only which one reads as
 * the title changes — both are always the same two fields, at the same two
 * testids, whichever language is on top.
 */
export const EditorHeader: React.FC<EditorHeaderProps> = ({
    nameKa,
    nameEn,
    onNameKaChange,
    onNameEnChange,
    categories,
    categoryId,
    onCategoryChange,
    locale,
    isReadOnly,
    subtitle,
    actions,
}) => {
    const { t } = useTranslation('pages');

    const kaField = (
        <TextField
            aria-label={t('operations.editor.nameKaAria')}
            className="min-w-0"
            isDisabled={isReadOnly}
            onChange={onNameKaChange}
            value={nameKa}
            {...getNodeTestIdProp('operation-editor-name-ka')}
        >
            <Input
                className={locale === 'ka' ? NAME_INPUT : SECONDARY_NAME_INPUT}
                placeholder={t('operations.editor.nameKaPlaceholder')}
            />
        </TextField>
    );
    const enField = (
        <TextField
            aria-label={t('operations.editor.nameEnAria')}
            className="min-w-0"
            isDisabled={isReadOnly}
            onChange={onNameEnChange}
            value={nameEn}
            {...getNodeTestIdProp('operation-editor-name-en')}
        >
            <Input
                className={locale === 'en' ? NAME_INPUT : SECONDARY_NAME_INPUT}
                placeholder={t('operations.editor.nameEnPlaceholder')}
            />
        </TextField>
    );

    return (
        <div className="flex flex-wrap items-end justify-between gap-4">
            <div className="flex min-w-0 flex-1 flex-col gap-1">
                {locale === 'ka' ? kaField : enField}
                {locale === 'ka' ? enField : kaField}
                {categories.length > 0 ? (
                    <Select
                        className="mt-2 w-56"
                        isDisabled={isReadOnly}
                        onSelectionChange={(id) =>
                            id && onCategoryChange(String(id))
                        }
                        selectedKey={categoryId || null}
                        {...getNodeTestIdProp('operation-editor-category')}
                    >
                        <Label className="text-xs text-muted">
                            {t('operations.editor.category')}
                        </Label>
                        <Select.Trigger>
                            <Select.Value />
                            <Select.Indicator />
                        </Select.Trigger>
                        <Select.Popover>
                            <ListBox>
                                {categoryOptions(categories, locale).map(
                                    (option) => (
                                        <ListBox.Item
                                            id={option.id}
                                            key={option.id}
                                            textValue={option.label}
                                        >
                                            {option.label}
                                            <ListBox.ItemIndicator />
                                        </ListBox.Item>
                                    ),
                                )}
                            </ListBox>
                        </Select.Popover>
                    </Select>
                ) : null}
                {subtitle.length > 0 ? (
                    <p
                        className="text-sm text-muted"
                        {...getNodeTestIdProp('operation-editor-subtitle')}
                    >
                        {subtitle
                            .map((clause) =>
                                clause.kind === 'text'
                                    ? clause.text
                                    : t(clause.key, clause.params),
                            )
                            .join(' · ')}
                    </p>
                ) : null}
            </div>
            {actions}
        </div>
    );
};
